import type { CfeDocument } from '../types/cfe';
import { formatLocalDate } from './localDateUtils';

function parseXml(value: string | null | undefined): XMLDocument {
  if (!value?.trim()) throw new Error('Falta el XML original del comprobante o de la firma.');
  const parser = new DOMParser();
  // Older records can contain an escaped XML string, or the full SOAP envelope.
  let xml = parser.parseFromString(value, 'application/xml');
  if (xml.querySelector('parsererror') && value.trim().startsWith('&lt;')) {
    const decoded = parser.parseFromString(`<value>${value}</value>`, 'application/xml').documentElement.textContent;
    xml = parser.parseFromString(decoded || '', 'application/xml');
  }
  if (xml.querySelector('parsererror')) throw new Error('El XML guardado está incompleto o no es válido.');
  const inner = xml.getElementsByTagNameNS('*', 'Xmlrespuesta')[0];
  if (inner) return parseXml(inner.textContent);
  return xml;
}

const text = (xml: Document | Element, name: string) =>
  xml.getElementsByTagNameNS('*', name)[0]?.textContent?.trim() || '';
const escapeHtml = (value: unknown) => String(value ?? '').replace(/[&<>"']/g,
  char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]!);

function required(xml: Document | Element, name: string): string {
  const value = text(xml, name);
  if (!value) throw new Error(`Falta ${name} en los datos fiscales guardados.`);
  return value;
}

function amount(value: string): string {
  if (!value.trim() || !Number.isFinite(Number(value))) throw new Error('El comprobante contiene un importe inválido.');
  return Number(value).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Uses the immutable emission and signature XML, never today's receipt calculations. */
export async function buildCfeThermalHtml(cfe: CfeDocument, width: 58 | 80 = 80): Promise<string> {
  if (Number(cfe.cfe_tipo_code) !== 101 || !Number(cfe.cfe_firmado_ok) || !Number(cfe.cfe_confirmado)) {
    throw new Error('La impresión térmica está disponible para e-tickets firmados y confirmados.');
  }
  if (['RECHAZADO', 'ANULADO', 'RECHAZADO_O_ANULADO'].includes(cfe.dgi_estado || '')) {
    throw new Error('No se puede imprimir como válido un e-ticket rechazado o anulado.');
  }
  const input = parseXml(cfe.xml_entrada);
  const signature = parseXml(cfe.xml_respuesta);
  if (required(input, 'IDDocTipoCFE') !== '101' || required(signature, 'FIRMADOOK') !== '1') {
    throw new Error('Los XML guardados no corresponden a un e-ticket firmado.');
  }
  const serie = required(signature, 'CAESERIE');
  const numero = required(signature, 'CAENRO');
  if (serie !== cfe.cfe_serie || Number(numero) !== Number(cfe.cfe_numero)) {
    throw new Error('La respuesta de firma no coincide con el número del e-ticket.');
  }
  const qr = required(signature, 'URLPARAVERIFICARQR');
  const qrUrl = new URL(qr);
  if (!['http:', 'https:'].includes(qrUrl.protocol)) throw new Error('La dirección del QR no es válida.');
  const { default: QRCode } = await import('qrcode');
  // Encode the exact string supplied by Taface, including its original escaping/parameters.
  const qrSvg = await QRCode.toString(qr, { type: 'svg', errorCorrectionLevel: 'M', margin: 4 });
  const currency = required(input, 'TOTTpoMoneda');
  const money = (value: string) => escapeHtml(amount(value));
  const row = (label: string, value: string) => `<div class="row"><span>${escapeHtml(label)}</span><span>${money(value)}</span></div>`;
  const detail = Array.from(input.getElementsByTagNameNS('*', 'Detalle'));
  if (!detail.length) throw new Error('Faltan los conceptos originales del e-ticket.');
  const lines = detail.map(line => `<section class="item"><strong>${escapeHtml(required(line, 'DETNomItem'))}</strong>
    <div class="row"><span>${escapeHtml(required(line, 'DETCantidad'))} × ${money(required(line, 'DETPrecioUnitario'))}</span>
    <span>${money(required(line, 'DETMontoItem'))}</span></div></section>`).join('');
  const taxFields = [
    ['No gravado', 'TOTMntNoGrv'], ['Neto IVA 10%', 'TOTMntNetoIvaTasaMin'],
    ['IVA 10%', 'TOTMntIVATasaMin'], ['Neto IVA 22%', 'TOTMntNetoIVATasaBasica'],
    ['IVA 22%', 'TOTMntIVATasaBasica'], ['Neto otra tasa', 'TOTMntNetoIVAOtra'],
    ['IVA otra tasa', 'TOTMntIVAOtra'], ['No facturable', 'TOTMontoNF'],
  ];
  const taxes = taxFields.filter(([, field]) => text(input, field) && Number(text(input, field)) !== 0)
    .map(([label, field]) => row(label, text(input, field))).join('');
  const emitter = required(input, 'EMIRznSoc');
  const ruc = required(input, 'EMIRUCEmisor');
  const cae = required(signature, 'CAENA');
  const expiry = required(signature, 'CAEVENCIMIENTO');
  const security = required(signature, 'CODSEGURIDAD');
  const emissionDate = formatLocalDate(required(input, 'IDDocFchEmis'));
  const expiryDate = formatLocalDate(expiry);
  if (emissionDate === '-' || expiryDate === '-') throw new Error('Las fechas fiscales guardadas no son válidas.');
  const state = cfe.dgi_estado === 'ACEPTADO' ? '' :
    `<p class="status">Estado DGI: ${escapeHtml(cfe.dgi_estado_taface || 'Pendiente de verificación')}</p>`;
  return `<!doctype html><html lang="es"><head><meta charset="UTF-8"><title>e-ticket ${escapeHtml(serie)}-${escapeHtml(numero)}</title>
  <style>
    @page { size: auto; margin: 0; }
    * { box-sizing: border-box; } body { margin: 0; background: #eee; color: #000; font: 12px Arial, sans-serif; }
    main { width: ${width}mm; padding: 3mm; margin: 12px auto; background: #fff; overflow-wrap: anywhere; }
    h1 { font-size: 16px; margin: 4px 0; } h2 { font-size: 15px; margin: 8px 0; }
    p { margin: 4px 0; } header, footer { text-align: center; } section { margin: 8px 0; }
    .divider { border-top: 1px dashed #000; padding-top: 8px; } .row { display: flex; justify-content: space-between; gap: 6px; margin: 4px 0; }
    .row > :last-child { white-space: nowrap; } .item, footer, .totals { break-inside: avoid; }
    .total { font-size: 15px; font-weight: bold; } .qr { width: 38mm; height: 38mm; margin: 4px auto; } .qr svg { width: 100%; height: 100%; }
    .toolbar { padding: 12px; text-align: center; } button, select { font: inherit; padding: 8px; margin: 4px; }
    .toolbar p { font-size: 13px; } .status { font-size: 11px; } footer { font-size: 11px; }
    @media print { body { background: #fff; } main { margin: 0; } .toolbar { display: none; } }
  </style><style id="thermal-paper-page"></style></head><body>
  <div class="toolbar"><label>Ancho del papel <select id="paper-width"><option value="80" ${width === 80 ? 'selected' : ''}>80 mm</option><option value="58" ${width === 58 ? 'selected' : ''}>58 mm</option></select></label>
    <button id="print-ticket">Imprimir e-ticket</button><p>Elegí el mismo ancho en la impresora, escala 100 %, sin encabezados ni pies de página.</p></div>
  <main><header><h1>${escapeHtml(emitter)}</h1><p>RUT ${escapeHtml(ruc)}</p>
    <p>${escapeHtml(required(input, 'EMIDomFiscal'))}</p><p>${escapeHtml(text(input, 'EMICiudad'))} ${escapeHtml(text(input, 'EMIDepartamento'))}</p>
    <h2>e-Ticket ${escapeHtml(serie)} ${escapeHtml(numero)}</h2><p>Fecha: ${escapeHtml(emissionDate)}</p>
    <p>${text(input, 'IDDocFmaPago') === '1' ? 'Contado' : text(input, 'IDDocFmaPago') === '2' ? 'Crédito' : ''} · ${escapeHtml(currency)}</p></header>
  <section class="divider"><p>${escapeHtml(text(input, 'RECRznSocRecep') || 'Consumidor final')}</p>
    ${(text(input, 'RECDocRecep') || text(input, 'RECDocRecepExtranjero')) ? `<p>Documento: ${escapeHtml(text(input, 'RECDocRecep') || text(input, 'RECDocRecepExtranjero'))}</p>` : ''}
    ${text(input, 'RECDirRecep') ? `<p>${escapeHtml(text(input, 'RECDirRecep'))}</p>` : ''}</section>
  <div class="divider">${lines}${text(input, 'IDDocMntBruto') === '1' ? '<p>Precios con IVA incluido</p>' : ''}</div><section class="divider totals">${taxes}
    <div class="total">${row(`TOTAL ${currency}`, required(input, 'TOTMntTotal'))}</div>
    ${text(input, 'TOTMntPagar') ? row('Monto a pagar', text(input, 'TOTMntPagar')) : ''}
    ${text(input, 'MPGlosaMP') ? `<p>Medio de pago: ${escapeHtml(text(input, 'MPGlosaMP'))}</p>` : ''}</section>
  <footer class="divider"><p>CAE ${escapeHtml(cae)}</p><p>Rango ${escapeHtml(required(signature, 'CAENROINICIAL'))} al ${escapeHtml(required(signature, 'CAENROFINAL'))}</p>
    <p>Vencimiento CAE: ${escapeHtml(expiryDate)}</p><p>Código de seguridad: ${escapeHtml(security)}</p>
    ${text(signature, 'RESOLUCIONIVA') ? `<p>Resolución IVA: ${escapeHtml(text(signature, 'RESOLUCIONIVA'))}</p>` : ''}
    <div class="qr" aria-label="QR de consulta del comprobante">${qrSvg}</div>
    <p>Verificá el comprobante con este QR</p><p>${escapeHtml(text(signature, 'URLPARAVERIFICARTEXTO'))}</p>${state}</footer>
  </main></body></html>`;
}

/** Reserve this window synchronously in the click handler to avoid popup blockers. */
export function showCfeThermalPreview(popup: Window, html: string): void {
  if (popup.closed) throw new Error('Se cerró la ventana de impresión.');
  popup.document.open();
  popup.document.write(html);
  popup.document.close();
  const updatePageSize = () => {
    const main = popup.document.querySelector('main')!;
    const width = (popup.document.getElementById('paper-width') as HTMLSelectElement).value === '58' ? 58 : 80;
    main.style.width = `${width}mm`;
    const height = Math.ceil(main.getBoundingClientRect().height * 25.4 / 96) + 1;
    popup.document.getElementById('thermal-paper-page')!.textContent = `@page { size: ${width}mm ${height}mm; margin: 0; }`;
  };
  updatePageSize();
  popup.addEventListener('beforeprint', updatePageSize);
  popup.document.getElementById('print-ticket')?.addEventListener('click', () => { updatePageSize(); popup.focus(); popup.print(); });
  popup.document.getElementById('paper-width')?.addEventListener('change', event => {
    (event.target as HTMLSelectElement).blur();
    updatePageSize();
  });
  popup.focus();
}
